import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, field, StringDecoder } from '@simonbackx/simple-encoding';
import { isSimpleError, isSimpleErrors, SimpleError, SimpleErrors } from '@simonbackx/simple-errors';
import { BalanceItemCartItem } from './BalanceItemCartItem.js';
import { DeleteRegisterItem, IDDeleteRegisterItem } from './DeleteRegisterItem.js';
import { IDRegisterItem } from './RegisterItem.js';
export class IDRegisterCart extends AutoEncoder {
    items = [];
    /**
     * todo: we should be able to move this to a Map
     */
    balanceItems = [];
    deleteRegistrations = [];
    get organizationId() {
        if (this.items.length > 0) {
            return this.items[0].organizationId;
        }
        if (this.balanceItems.length > 0) {
            return this.balanceItems[0].item.organizationId;
        }
        // for deleted registrations, we don't have the id at the moment
        // that is not a disaster since we don't need to load these carts from storage
        return null;
    }
    hydrate(context) {
        const cart = new RegisterCart();
        cart.items = this.items.map(i => i.hydrate(context));
        cart.balanceItems = this.balanceItems;
        cart.deleteRegistrations = this.deleteRegistrations.map(i => i.hydrate(context));
        return cart;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(IDRegisterItem) })
], IDRegisterCart.prototype, "items", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemCartItem), optional: true })
], IDRegisterCart.prototype, "balanceItems", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), optional: true, field: 'deleteRegistrationIds' }),
    field({
        decoder: new ArrayDecoder(IDDeleteRegisterItem),
        optional: true,
        version: 423,
        upgrade: (registrationIds) => (registrationIds ?? []).map(registrationId => IDDeleteRegisterItem.create({ registrationId })),
        downgrade: (items) => items.map(i => i.registrationId),
    })
], IDRegisterCart.prototype, "deleteRegistrations", void 0);
export class RegisterCart {
    items = [];
    balanceItems = [];
    /**
     * You can define which registrations you want remove as part of this register operation.
     */
    deleteRegistrations = [];
    bundleDiscounts = [];
    calculatePrices() {
        for (const item of this.items) {
            item.calculatePrice();
        }
        // Now calculate discounts
        this.bundleDiscounts = [];
        if (this.singleOrganization) {
            for (const bundleDiscount of this.singleOrganization.period.settings.bundleDiscounts) {
                const grouped = bundleDiscount.calculate(this, this.singleOrganization.period.period.id);
                for (const [_, calculation] of grouped.calculations) {
                    if (calculation.netTotal !== 0) {
                        this.bundleDiscounts.push(calculation);
                    }
                }
            }
        }
    }
    clone() {
        const cart = new RegisterCart();
        cart.items = this.items.map(i => i.clone());
        cart.balanceItems = this.balanceItems.map(i => i.clone());
        cart.deleteRegistrations = this.deleteRegistrations.map(r => r.clone());
        return cart;
    }
    convert() {
        return IDRegisterCart.create({
            items: this.items.map(i => i.convert()),
            balanceItems: this.balanceItems,
            deleteRegistrations: this.deleteRegistrations.map(r => r.convert()),
        });
    }
    add(item) {
        if (this.contains(item)) {
            return this.remove(item, item);
        }
        if (!this.canAdd(item)) {
            return;
        }
        this.items.push(item);
    }
    canAdd(item) {
        if (this.contains(item)) {
            return false;
        }
        if (this.items.length >= 500) {
            return false;
        }
        return true;
    }
    contains(item) {
        for (const [i, otherItem] of this.items.entries()) {
            if (otherItem.id === item.id || (otherItem.member.id === item.member.id && otherItem.groupId === item.groupId)) {
                return true;
            }
        }
        return false;
    }
    getMemberAndGroup(memberId, groupId) {
        for (const [i, otherItem] of this.items.entries()) {
            if (otherItem.member.id === memberId && otherItem.groupId === groupId) {
                return otherItem;
            }
        }
        return null;
    }
    containsMemberAndGroup(memberId, groupId) {
        return this.getMemberAndGroup(memberId, groupId) !== null;
    }
    remove(item, replaceWith) {
        for (const [i, otherItem] of this.items.entries()) {
            if (otherItem.id === item.id || (otherItem.member.id === item.member.id && otherItem.groupId === item.groupId)) {
                this.items.splice(i, 1, ...(replaceWith ? [replaceWith] : []));
                break;
            }
        }
    }
    removeMemberAndGroup(memberId, groupId) {
        for (let i = this.items.length - 1; i >= 0; i--) {
            const otherItem = this.items[i];
            if (otherItem.member.id === memberId && otherItem.groupId === groupId) {
                this.items.splice(i, 1);
            }
        }
    }
    removeRegistration(registration) {
        const index = this.deleteRegistrations.findIndex(r => r.id === registration.registration.id);
        if (index === -1) {
            const first = this.deleteRegistrations[0];
            this.deleteRegistrations.push(first
                ? new DeleteRegisterItem({ registration, cancellationFeePercentage: first.cancellationFeePercentage })
                : DeleteRegisterItem.defaultFor(registration));
        }
    }
    unremoveRegistration(registration) {
        const index = this.deleteRegistrations.findIndex(r => r.id === registration.registration.id);
        if (index !== -1) {
            this.deleteRegistrations.splice(index, 1);
        }
    }
    canAddBalanceItem(item) {
        if (this.singleOrganization) {
            if (item.item.organizationId !== this.singleOrganization?.id) {
                return false;
            }
        }
        // Check duplicate
        for (const i of this.balanceItems) {
            if (i.item.id === item.item.id) {
                return false;
            }
        }
        return true;
    }
    addBalanceItem(item) {
        if (!this.canAddBalanceItem(item)) {
            return;
        }
        this.balanceItems.push(item);
    }
    removeBalanceItem(item) {
        for (let i = this.balanceItems.length - 1; i >= 0; i--) {
            if (this.balanceItems[i].id === item.id) {
                this.balanceItems.splice(i, 1);
            }
        }
    }
    get isEmpty() {
        return this.count === 0;
    }
    get count() {
        return this.items.length + this.balanceItems.length + this.deleteRegistrations.length;
    }
    get price() {
        return this.items.reduce((total, item) => item.calculatedPrice + total, 0)
            + this.balanceItems.reduce((total, item) => {
                return total + item.price;
            }, 0);
    }
    get balanceItemDiscounts() {
        return this.balanceItems.filter(b => b.price < 0);
    }
    get balanceItemDiscountsPrice() {
        return this.balanceItemDiscounts.reduce((total, item) => {
            return total + item.price;
        }, 0);
    }
    get priceDueLater() {
        return this.items.reduce((total, item) => item.calculatedPriceDueLater + total, 0);
    }
    /**
     * Discounts that will be applied to items that are due now
     * (net, so minus the already applied discounts)
     */
    get bundleDiscount() {
        return this.bundleDiscounts.map(d => d.netTotalDueNow).reduce((a, b) => a + b, 0);
    }
    /**
     * Discounts that will be applied to items that are due later
     * (net, so minus the already applied discounts)
     */
    get bundleDiscountDueLater() {
        return this.bundleDiscounts.map(d => d.netTotalDueLater).reduce((a, b) => a + b, 0);
    }
    get refund() {
        return this.items.reduce((total, item) => item.calculatedRefund + total, 0)
            + this.deleteRegistrations.reduce((total, item) => {
                return total + item.registration.registration.calculatedPrice;
            }, 0);
    }
    get cancellationFees() {
        return this.deleteRegistrations.reduce((total, item) => total + item.cancellationFee, 0);
    }
    get singleOrganization() {
        if (this.items.length === 0) {
            if (this.deleteRegistrations.length > 0) {
                const organizationId = this.deleteRegistrations[0].registration.group.organizationId;
                return this.deleteRegistrations[0].registration.member.organizations.find(o => o.id === organizationId) ?? null;
            }
            return null;
        }
        return this.items[0].organization;
    }
    validate(checkout, data) {
        const newItems = [];
        const errors = new SimpleErrors();
        for (const item of this.items) {
            try {
                item.validate({ final: true });
                item.cartError = null;
                newItems.push(item);
                const isDuplicate = !!this.items.find(i => i !== item && i.isSameRegistration(item));
                if (isDuplicate) {
                    errors.addError(new SimpleError({
                        code: 'duplicate_register_item',
                        message: 'duplicate register item',
                        human: $t(`%qo`),
                    }));
                }
            }
            catch (e) {
                if (isSimpleError(e) || isSimpleErrors(e)) {
                    e.addNamespace('cart');
                    errors.addError(e);
                }
                else {
                    throw e;
                }
                if (isSimpleError(e) && e.meta?.recoverable) {
                    item.cartError = e;
                    newItems.push(item);
                }
            }
        }
        const cleanedBalanceItems = [];
        for (const balanceItem of this.balanceItems) {
            // TODO: validate balance item organization (happens in backend anyway)
            if (checkout.singleOrganizationId && balanceItem.item.organizationId !== checkout.singleOrganizationId) {
                errors.addError(new SimpleError({
                    code: 'invalid_organization',
                    message: 'Invalid organization in balanceItems',
                    human: $t(`%qp`),
                    field: 'balanceItems',
                }));
                continue;
            }
            try {
                balanceItem.validate({ balanceItems: data?.memberBalanceItems });
                cleanedBalanceItems.push(balanceItem);
            }
            catch (e) {
                if (isSimpleError(e) || isSimpleErrors(e)) {
                    e.addNamespace('cart');
                    errors.addError(e);
                }
                else {
                    throw e;
                }
            }
        }
        const cleanedRegistrations = [];
        const singleOrganization = checkout.singleOrganization;
        for (const deleteItem of this.deleteRegistrations) {
            const registration = deleteItem.registration;
            if (checkout.singleOrganizationId && registration.registration.group.organizationId !== checkout.singleOrganizationId) {
                errors.addError(new SimpleError({
                    code: 'invalid_organization',
                    message: 'Invalid organization in deleteRegistrations',
                    human: $t(`%qq`),
                    field: 'deleteRegistrations',
                }));
                continue;
            }
            if (!singleOrganization) {
                continue;
            }
            const platform = registration.member.platform;
            const periodId = registration.registration.group.periodId;
            const period = periodId === platform.period.id ? platform.period : (periodId === singleOrganization.period.period.id ? singleOrganization.period.period : registration.registration.group.settings.period);
            if (period && period.locked) {
                errors.addError(new SimpleError({
                    code: 'locked_period',
                    message: 'Locked period',
                    human: $t('%ql', { group: registration.registration.group.settings.name, period: period.nameShort }),
                }));
                continue;
            }
            if (!period) {
                errors.addError(new SimpleError({
                    code: 'locked_period',
                    message: 'Locked period',
                    human: $t('%qm', { group: registration.registration.group.settings.name }),
                }));
                continue;
            }
            try {
                deleteItem.validate();
            }
            catch (e) {
                if (isSimpleError(e) || isSimpleErrors(e)) {
                    e.addNamespace('deleteRegistrations');
                    errors.addError(e);
                    continue;
                }
                throw e;
            }
            cleanedRegistrations.push(deleteItem);
        }
        this.balanceItems = cleanedBalanceItems;
        this.deleteRegistrations = cleanedRegistrations;
        this.items = newItems;
        errors.throwIfNotEmpty();
    }
}
//# sourceMappingURL=RegisterCart.js.map